import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { buildQueryString } from "utils/queryHelpers";

const LabelContext = createContext();

export const LabelProvider = ({ children }) => {
  const [labels, setLabels] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
  });

  // ✅ Gunakan useCallback untuk mencegah re-render tidak perlu
  const fetchLabels = useCallback(async (search = "", page = 1) => {
    setLabels((prev) => ({ ...prev, loading: true }));

    const queryString = buildQueryString({ search, page, limit: 40 });

    try {
      const response = await getRequest(`/label${queryString}`);
      if (response.success) {
        setLabels({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });
      } else {
        throw new Error("Failed to fetch labels");
      }
    } catch (error) {
      setLabels((prev) => ({ ...prev, error: error.message, loading: false }));
    }
  }, []);

  // ✅ Gunakan useEffect untuk pemanggilan API hanya saat filterOptions berubah
  useEffect(() => {
    fetchLabels(filterOptions.search, filterOptions.currentPage);
  }, [fetchLabels, filterOptions]);

  // ✅ Handle perubahan pencarian dengan debounce
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const searchValue = e.target.value.trim();
      if (searchValue !== filterOptions.search) {
        setFilterOptions((prev) => ({ ...prev, search: searchValue }));
      }
    }
  };

  // ✅ Handle perubahan halaman
  const handlePageChange = (page) => {
    setFilterOptions((prev) => ({ ...prev, currentPage: page }));
  };

  const Pagination = usePaginationWithComponent({
    totalPages: labels.pagination?.last_page || 1,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <LabelContext.Provider
      value={{
        labels,
        handleSearch,
        filterOptions,
        refetch: () => fetchLabels(filterOptions.search, filterOptions.currentPage),
        Pagination,
        fetchOptions: async (inputValue) => {
          const response = await getRequest(`/label?search=${inputValue}`);
          return response.success
            ? response.data.map((label) => ({
                value: label.id,
                label: label.name,
              }))
            : [];
        },
      }}
    >
      {children}
    </LabelContext.Provider>
  );
};

export const useLabel = () => {
  const context = useContext(LabelContext);
  if (!context) {
    throw new Error("useLabel must be used within LabelProvider");
  }
  return context;
};
